import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  BookOpenIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import {
  createJournal,
  fetchJournals,
} from "../../../api/journaling";
import JournalDetailDialog from "./JournalDetailDialog";
import SubjectLinkFilter, {
  buildSubjectFilterParams,
} from "../shared/SubjectLinkFilter";
import { debounce } from "lodash";

const COLOR_OPTIONS = [
  "#0EA5E9",
  "#6366F1",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#64748B",
];

function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return null;
  }
}

/**
 * Reusable journal browser. When +subjectType+/+subjectId+ are supplied the
 * list is scoped to that subject (e.g. an employee, customer or project) and
 * new journals are attached to it; otherwise it lists all company journals.
 */
export default function JournalSection({ subjectType, subjectId, title, subtitle }) {
  const { t } = useTranslation();
  const isScoped = Boolean(subjectType && subjectId != null);
  const [journals, setJournals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailJournal, setDetailJournal] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", color: COLOR_OPTIONS[0] });
  const [isSaving, setIsSaving] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    subjectType: "",
    subjectId: null,
    subjectLabel: "",
  });

  const loadJournals = useCallback(async (nextFilters = filters) => {
    setIsLoading(true);
    const params = { per_page: 100 };
    if (isScoped) {
      params.subject_type = subjectType;
      params.subject_id = subjectId;
    } else {
      Object.assign(params, buildSubjectFilterParams(nextFilters));
    }
    const res = await fetchJournals(params);
    setJournals(res?.data || []);
    setIsLoading(false);
  }, [isScoped, subjectType, subjectId, filters]);

  const debouncedLoad = useMemo(
    () => debounce((nextFilters) => loadJournals(nextFilters), 250),
    [loadJournals]
  );

  useEffect(() => {
    if (isScoped) {
      loadJournals();
      return undefined;
    }
    debouncedLoad(filters);
    return () => debouncedLoad.cancel();
  }, [isScoped, filters, loadJournals, debouncedLoad]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) return;
    setIsSaving(true);
    const payload = { ...form };
    if (subjectType && subjectId != null) {
      payload.subject_type = subjectType;
      payload.subject_id = subjectId;
    }
    const res = await createJournal(payload);
    setIsSaving(false);
    if (res?.errors) {
      toast.error(t("journal.save_failed"));
      return;
    }
    setCreateOpen(false);
    setForm({ title: "", description: "", color: COLOR_OPTIONS[0] });
    setJournals((prev) => [res, ...prev]);
    setDetailJournal(res);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {title || t("journal.journals")}
          </h3>
          <p className="text-sm text-slate-500">
            {subtitle || t("journal.section_subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
        >
          <PlusIcon className="h-4 w-4" />
          {t("journal.new_journal")}
        </button>
      </div>

      {!isScoped ? (
        <SubjectLinkFilter
          filters={filters}
          onChange={setFilters}
          searchPlaceholder={t("journal.search_placeholder")}
        />
      ) : null}

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-slate-600" />
          </div>
        ) : journals.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center">
            <BookOpenIcon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-700">
              {t("journal.no_journals")}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {t("journal.no_journals_hint")}
            </p>
          </div>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {journals.map((journal) => (
              <li key={journal.id}>
                <button
                  type="button"
                  onClick={() => setDetailJournal(journal)}
                  className="flex w-full items-start gap-3 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <span
                    className="mt-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-white"
                    style={{ backgroundColor: journal.color || COLOR_OPTIONS[0] }}
                  >
                    <BookOpenIcon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-900">
                      {journal.title}
                    </span>
                    {journal.description ? (
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        {journal.description}
                      </span>
                    ) : null}
                    <span className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1">
                        <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                        {journal.entries_count || 0}
                      </span>
                      {!subjectType && journal.subject_label ? (
                        <span className="truncate">{journal.subject_label}</span>
                      ) : null}
                      {formatDate(journal.last_entry_at) ? (
                        <span>{formatDate(journal.last_entry_at)}</span>
                      ) : null}
                      {journal.last_sent_at ? (
                        <span className="text-emerald-700">
                          {t("documentationSend.sent_short")}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <PMDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("journal.new_journal")}
        size="lg"
        closeOnOutsideClick
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("Title")}
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              required
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("Description")}
            </label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("journal.color")}
            </label>
            <div className="mt-2 flex gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, color }))}
                  className={`h-8 w-8 rounded-full border-2 transition ${
                    form.color === color ? "border-slate-900" : "border-transparent"
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
            >
              {isSaving ? t("Saving...") : t("Save")}
            </button>
          </div>
        </form>
      </PMDialog>

      <JournalDetailDialog
        open={Boolean(detailJournal)}
        onClose={() => {
          setDetailJournal(null);
          loadJournals();
        }}
        journal={detailJournal}
        onUpdated={(updated) => {
          if (!updated?.id) return;
          setDetailJournal((prev) => (prev ? { ...prev, ...updated } : updated));
          setJournals((prev) =>
            prev.map((j) => (j.id === updated.id ? { ...j, ...updated } : j))
          );
        }}
      />
    </div>
  );
}
